import {
  EventDetailsForView,
  EventWithCustomField,
  ParticipationRequestType,
} from "@ac-expo/api-types";
import { formatEventDate } from "@ac-expo/utils";
import { InfoIcon, ShareFatIcon } from "@phosphor-icons/react";
import { TooltipContent, TooltipTrigger } from "@radix-ui/react-tooltip";
import { noop } from "lodash";
import { ReactNode, useMemo } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { cn } from "../../../utils";
import { Button } from "../Button";
import { Tooltip, TooltipProvider } from "../Tooltip";
import { useToast } from "../useToast";

type EventViewHeaderProps = {
  event: EventWithCustomField<EventDetailsForView>;
  getWebAppUrl: () => string;
  toggleFavoriteButton: ReactNode;
  as: "organiser" | "exhibitor" | "visitor";
  isEventInThePast: boolean;
  eventAdminId: string;
  participationRequestStatus: ParticipationRequestType["status"] | null;
  sendParticipationRequest?: () => void;
};

export function EventViewHeaderCard({
  event,
  getWebAppUrl,
  toggleFavoriteButton = null,
  as,
  isEventInThePast,
  participationRequestStatus,
  sendParticipationRequest,
}: EventViewHeaderProps) {
  const onSendParticipationRequest = sendParticipationRequest ?? noop;
  const asProfessional = ["exhibitor", "organiser"].includes(as);

  const { toast } = useToast();
  return (
    <div className="w-full lg:col-span-2">
      <div className="mt-4 hidden w-full flex-wrap items-center justify-between gap-2 px-3 py-[6px] lg:flex">
        <div className="rounded-full border lg:mt-0 lg:inline-block">
          <p className="whitespace-nowrap px-2 text-lg font-bold text-gray-600">
            {formatEventDate(
              event.dates?.schedules ?? [],
              event.dates?.exceptionalClosingDates ?? [],
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <CopyToClipboard
            text={getWebAppUrl()}
            onCopy={() => toast({ title: "Lien copié dans le presse-papier" })}
          >
            <Button variant={"outline-blue"} className="flex flex-row gap-2" type="button">
              <ShareFatIcon size={16} className="text-primary-500" />
              <span className="text-primary-500">Partager</span>
            </Button>
          </CopyToClipboard>
          {toggleFavoriteButton}
        </div>
      </div>
      <div className="flex flex-row items-center justify-between">
        <h1 className="text-wrap break-words px-[14px] text-2xl font-extrabold lg:px-0 lg:pt-4 lg:text-4xl">
          {event.name}
        </h1>
        <div className="flex flex-row lg:hidden px-4">
          <CopyToClipboard
            text={getWebAppUrl()}
            onCopy={() => toast({ title: "Lien copié dans le presse-papier" })}
          >
            <Button variant="null" type="button" className="p-2">
              <ShareFatIcon size={24} weight="fill" className="text-primary-500" />
            </Button>
          </CopyToClipboard>
          {toggleFavoriteButton}
        </div>
      </div>
      {asProfessional && event.organiser.adminId !== "AC-EXPO" && (
        <div className="mx-1 mt-2 flex-col items-center gap-8 lg:flex">
          <ParticipationRequestButton
            isEventInThePast={isEventInThePast}
            sendParticipationRequest={onSendParticipationRequest}
            requestState={participationRequestStatus}
            disabled={as === "organiser"}
            className="h-full"
          />
        </div>
      )}
      <p className="hidden whitespace-pre-line px-[14px] text-sm font-medium lg:block lg:px-0 lg:pt-4">
        {event.description}
      </p>
    </div>
  );
}

type ParticipationRequestButtonProps = {
  disabled?: boolean;
  isEventInThePast: boolean;
  requestState: ParticipationRequestType["status"] | null;
  className?: string;
  sendParticipationRequest?: () => void;
};

export const ParticipationRequestButton = ({
  sendParticipationRequest,
  isEventInThePast,
  requestState,
  disabled,
  className,
}: ParticipationRequestButtonProps) => {
  const isRequested = !!requestState;
  const requestStatus = useMemo(() => displayRequestStatus(requestState), [requestState]);
  return (
    <Button
      onClick={sendParticipationRequest}
      variant={isRequested || isEventInThePast ? "disabled" : "outline-white"}
      className={cn("w-full rounded-lg border p-4 shadow-none", className)}
      disabled={disabled || isRequested}
    >
      {isEventInThePast && !isRequested ? "Événement passé" : requestStatus}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger className="ml-2" asChild>
            <InfoIcon size={16} />
          </TooltipTrigger>
          <TooltipContent className="w-fit rounded-lg border bg-white px-4 py-2 text-black">
            Envoie une demande de participation à l&apos;événement en tant qu&apos;exposant
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </Button>
  );
};

const displayRequestStatus = (status: ParticipationRequestType["status"] | null) => {
  switch (status) {
    case "accepted":
      return "Demande acceptée";
    case "pending":
      return "Demande en attente";
    case "refused":
      return "Demande refusée";
    default:
      return "Demande de participation";
  }
};
